import { useTranslation } from "react-i18next";
import { NavLink, Outlet } from "react-router-dom";

import { useMe } from "@/api/me";
import { cn } from "@/lib/utils";

interface RailItem {
  to: string;
  labelKey: string;
  adminOnly?: boolean;
}

interface RailSection {
  headerKey: string;
  items: RailItem[];
}

const SECTIONS: RailSection[] = [
  {
    headerKey: "settings.sections.configuration",
    items: [
      {
        to: "/settings/forecasting",
        labelKey: "settings.rail.forecasting",
        adminOnly: true,
      },
      {
        to: "/settings/cleansing",
        labelKey: "settings.rail.cleansing",
        adminOnly: true,
      },
      {
        to: "/settings/company",
        labelKey: "settings.rail.company",
        adminOnly: true,
      },
      {
        to: "/settings/planning",
        labelKey: "settings.rail.planning",
        adminOnly: true,
      },
    ],
  },
  {
    headerKey: "settings.sections.workspace",
    items: [
      {
        to: "/settings/users",
        labelKey: "settings.rail.users",
        adminOnly: true,
      },
      { to: "/settings/tags", labelKey: "settings.rail.tags" },
      { to: "/settings/suppliers", labelKey: "settings.rail.suppliers" },
      { to: "/settings/integrations", labelKey: "settings.rail.integrations" },
    ],
  },
];

export default function SettingsLayout() {
  const { t } = useTranslation();
  const meQuery = useMe();
  const isAdmin = meQuery.data?.isStaff ?? false;

  return (
    <div className="mx-auto max-w-5xl py-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
          {t("settings.title")}
        </h1>
        <p className="mt-1 text-sm text-gray-600">{t("settings.subtitle")}</p>
      </header>

      <div className="grid grid-cols-[200px_1fr] gap-10">
        <nav aria-label={t("settings.title")}>
          {SECTIONS.map((section) => {
            const visibleItems = section.items.filter(
              (i) => !i.adminOnly || isAdmin,
            );
            if (visibleItems.length === 0) return null;
            return (
              <div key={section.headerKey} className="mb-6">
                <h2 className="mb-2 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                  {t(section.headerKey)}
                </h2>
                <ul className="space-y-1">
                  {visibleItems.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end
                        className={({ isActive }) =>
                          cn(
                            "block rounded px-2 py-1 text-sm transition-colors",
                            isActive
                              ? "bg-gray-100 font-medium text-gray-900"
                              : "text-gray-700 hover:bg-gray-50",
                          )
                        }
                      >
                        {t(item.labelKey)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
